import React, { useState, useEffect } from "react";
import { Button, Col, DatePicker, Flex, Form, Input, Row, message } from "antd";
import { Location } from "../../types/location";
import EnvelopeSVG from "../../assets/teamMember/EnvelopeSVG";
import PhoneSVG from "../../assets/teamMember/PhoneSVG";
import ContactsSVG from "../../assets/teamMember/ContactsSVG";
import LocationCheckSVG from "../../assets/teamMember/LocationCheckSVG";
import TimerSVG from "../../assets/teamMember/TimerSVG";
import PinSVG from "../../assets/teamMember/PinSVG";
import EditSVG from "../../assets/teamMember/EditSVG";
import { editTeamMember } from "../../network/teamMember";
import { useMutation } from "@tanstack/react-query";
import queryClient from "../../services/react-query/queryClient";
import { queryKeys } from "../../services/react-query/queryKeys";
import PhoneInputWithCountrySelect, { Country } from "react-phone-number-input";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { checkStringInLanguage } from "../../utils/utils";

interface BasicInfoProps {
  teamMemberData: any;
  editMode?: boolean;
}

const BasicInfo: React.FC<BasicInfoProps> = ({ teamMemberData, editMode }) => {
  const intl = useIntl();
  const [editing, setEditing] = useState(editMode || false);
  const [form] = Form.useForm();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");

  const { currentLang } = useSelector(
    (state: { user: { currentLang: "en" | "ar" } }) => {
      return {
        currentLang: state.user.currentLang,
      };
    }
  );
  const { mutate: editMutation, isLoading } = useMutation<
    void,
    unknown,
    { location_permissions: { location_id: number; permission: string }[] }
  >((params) => editTeamMember(teamMemberData?.id, params), {
    onSuccess: (res) => {
      message.success("Edit success!");
      queryClient.invalidateQueries([queryKeys.listSpacficTeamMemer]);
      queryClient.invalidateQueries([queryKeys.listTeamMember]);
      setEditing(false);
    },
    onError: (error) => {
      message.error(
        intl.formatMessage({ id: "tryAgain", defaultMessage: "حاول مرة اخرى" })
      );
    },
  });

  const onFinish = async (values: any) => {
    const { phone, email, ...dataWithoutPhoneAndEmail } = values;
    const requestData = {
      ...dataWithoutPhoneAndEmail,
      start_date: dataWithoutPhoneAndEmail["start_date"].format("YYYY-MM-DD"),
      end_date: dataWithoutPhoneAndEmail["end_date"]
        ? dataWithoutPhoneAndEmail["end_date"].format("YYYY-MM-DD")
        : null,
    };
    if (email !== teamMemberData?.email) {
      requestData["email"] = email;
    }
    editMutation(requestData);
  };

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumber(value);
  };

  const handleCountryChange = (country: Country) => {
    setSelectedCountry(country);
  };

  const start_date = dayjs(teamMemberData?.start_date);
  const end_date = teamMemberData?.end_date
    ? dayjs(teamMemberData.end_date)
    : null;
  return (
    <>
      <div className="tabs !bg-[#EEF0FF] p-4">
        <Flex justify="flex-end">
          <Button
            onClick={() => setEditing(!editing)}
            className="border-none bg-transparent shadow-none outline-none"
          >
            <EditSVG />
          </Button>
        </Flex>

        {!editing ? (
          <>
            <div className="mb-3 bg-white rounded-md p-2">
              <div className="flex flex-col md:flex-row gap-2 items-center justify-center md:justify-between">
                <Flex justify="flex-start">
                  <div className="text-lg space-x-2">
                    <ContactsSVG />
                    <span className="">
                      {" "}
                      {teamMemberData?.title[currentLang] || ""}
                    </span>
                  </div>
                </Flex>
                <Flex justify="center">
                  <div className="text-lg space-x-2">
                    <PhoneSVG />
                    <span dir="ltr"> {teamMemberData?.phone}</span>
                  </div>
                </Flex>
                <Flex justify="flex-end">
                  <div className="text-lg space-x-2">
                    <EnvelopeSVG />
                    <span className="mx-2"> {teamMemberData?.email}</span>
                  </div>
                </Flex>
              </div>
            </div>

            {teamMemberData?.locations.map(
              (location: Location & { name: { ar: string; en: string } }) => (
                <div
                  key={location?.id}
                  className="mb-3 bg-white rounded-md p-2"
                >
                  <div className="flex flex-col md:flex-row justify-center items-center gap-2 md:justify-between">
                    <Flex justify="flex-start">
                      <div className="text-color-gray text-base font-normal">
                        <TimerSVG />{" "}
                        {intl.formatMessage({
                          id: "startDate",
                          defaultMessage: "تاريخ البدء",
                        })}
                        :
                      </div>
                      <div className="mx-2">{teamMemberData?.start_date}</div>
                    </Flex>

                    <Flex justify="flex-end">
                      <div className="text-color-gray text-base font-normal">
                        <LocationCheckSVG />{" "}
                        {intl.formatMessage({
                          id: "branch",
                          defaultMessage: "الفرع",
                        })}
                        :
                      </div>
                      <div className="mx-2">
                        {location?.name[currentLang] || ""}
                      </div>
                    </Flex>
                  </div>
                </div>
              )
            )}

            <div className="mb-3 bg-white rounded-md p-2">
              <Flex justify="flex-start">
                <div className="text-color-gray text-base font-normal ">
                  <PinSVG />
                  {intl.formatMessage({
                    id: "notes",
                    defaultMessage: "ملاحظات",
                  })}
                  :
                </div>
                <div>{teamMemberData?.notes[currentLang] || ""}</div>
              </Flex>
            </div>
          </>
        ) : (
          <div>
            <Form
              layout="vertical"
              className="bg-color-light-gr p-4 rounded-md"
              onFinish={onFinish}
              initialValues={{
                name: {
                  ar: teamMemberData?.name?.ar,
                  en: teamMemberData?.name?.en,
                },
                title: {
                  ar: teamMemberData?.title["ar"],
                  en: teamMemberData?.title["en"],
                },
                email: teamMemberData?.email,
                phone: teamMemberData?.phone,
                notes: {
                  ar: teamMemberData?.notes["ar"],
                },
                start_date: start_date,
                end_date: end_date,
              }}
              form={form}
            >
              <Row gutter={{ xs: 8, sm: 16, md: 8, lg: 18 }}>
                <Col
                  className="gutter-row"
                  xl={8}
                  lg={12}
                  md={12}
                  xs={24}
                  sm={8}
                  span={11}
                >
                  <Form.Item
                    name={["name", "ar"]}
                    label={intl.formatMessage({
                      id: "memberNameAr",
                    })}
                    required={false}
                    className="form-style w-full"
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "memberNameRequired",
                          defaultMessage: "اسم العضو مطلوب",
                        }),
                      },
                      {
                        type: "string",
                        min: 2,
                        message: intl.formatMessage({
                          id: "memberNameRequired2Chars",
                          defaultMessage:
                            "يجب أن يحتوي الاسم على ما لا يقل عن 2 أحرف",
                        }),
                      },
                      {
                        validator: (_, value) => {
                          if (value) {
                            if (checkStringInLanguage(value, "ar")) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject(
                                new Error(
                                  intl.formatMessage({
                                    id: "service_name_arabic_error",
                                  })
                                )
                              );
                            }
                          }
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder={intl.formatMessage({
                        id: "enterMemberName",
                        defaultMessage: "ادخل اسم العضو",
                      })}
                    />
                  </Form.Item>
                </Col>
                <Col
                  className="gutter-row"
                  xl={8}
                  lg={12}
                  md={12}
                  xs={24}
                  sm={8}
                  span={11}
                >
                  <Form.Item
                    name={["name", "en"]}
                    label={intl.formatMessage({
                      id: "memberNameEn",
                    })}
                    required={false}
                    className="form-style w-full"
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "memberNameRequired",
                          defaultMessage: "اسم العضو مطلوب",
                        }),
                      },
                      {
                        type: "string",
                        min: 2,
                        message: intl.formatMessage({
                          id: "memberNameRequired2Chars",
                          defaultMessage:
                            "يجب أن يحتوي الاسم على ما لا يقل عن 2 أحرف",
                        }),
                      },
                      {
                        validator: (_, value) => {
                          if (value) {
                            if (checkStringInLanguage(value, "en")) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject(
                                new Error(
                                  intl.formatMessage({
                                    id: "service_name_english_error",
                                  })
                                )
                              );
                            }
                          }
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder={intl.formatMessage({
                        id: "enterMemberName",
                        defaultMessage: "ادخل اسم العضو",
                      })}
                    />
                  </Form.Item>
                </Col>
                <Col
                  className="gutter-row"
                  xl={8}
                  lg={12}
                  md={12}
                  xs={24}
                  sm={8}
                  span={11}
                >
                  <Form.Item
                    name={["title", "ar"]}
                    label={intl.formatMessage({
                      id: "memberJobAr",
                      defaultMessage: "وظيفة العضو",
                    })}
                    className="w-full"
                    required={false}
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "memberJobRequired",
                          defaultMessage: "وظيفة العضو مطلوبة",
                        }),
                      },
                      {
                        validator: (_, value) => {
                          if (value) {
                            if (checkStringInLanguage(value, "ar")) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject(
                                new Error(
                                  intl.formatMessage({
                                    id: "service_name_arabic_error",
                                  })
                                )
                              );
                            }
                          }
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder={intl.formatMessage({
                        id: "enterMemberJob",
                        defaultMessage: "ادخل وظيفة العضو ",
                      })}
                    />
                  </Form.Item>
                </Col>
                <Col
                  className="gutter-row"
                  xl={8}
                  lg={12}
                  md={12}
                  xs={24}
                  sm={8}
                  span={11}
                >
                  <Form.Item
                    name={["title", "en"]}
                    label={intl.formatMessage({
                      id: "memberJobEn",
                      defaultMessage: "وظيفة العضو",
                    })}
                    className="w-full"
                    required={false}
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "memberJobRequired",
                          defaultMessage: "وظيفة العضو مطلوبة",
                        }),
                      },
                      {
                        validator: (_, value) => {
                          if (value) {
                            if (checkStringInLanguage(value, "en")) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject(
                                new Error(
                                  intl.formatMessage({
                                    id: "service_name_english_error",
                                  })
                                )
                              );
                            }
                          }
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder={intl.formatMessage({
                        id: "enterMemberJob",
                        defaultMessage: "ادخل وظيفة العضو ",
                      })}
                    />
                  </Form.Item>
                </Col>
                <Col
                  className="gutter-row"
                  xl={8}
                  lg={12}
                  md={12}
                  xs={24}
                  sm={8}
                  span={11}
                >
                  <Form.Item
                    label={intl.formatMessage({
                      id: "email",
                      defaultMessage: "البريد الإلكتروني",
                    })}
                    required={false}
                    name="email"
                    className="form-style w-full"
                    rules={[
                      // {
                      //   required: true,
                      //   message: intl.formatMessage({
                      //     id: "emailRequired",
                      //     defaultMessage: " البريد الإلكتروني مطلوب",
                      //   }),
                      // },
                      {
                        type: "email",
                        message: intl.formatMessage({
                          id: "invalidEmail",
                          defaultMessage: "البريد الإلكتروني غير صالح",
                        }),
                      },
                    ]}
                  >
                    <Input
                      placeholder={intl.formatMessage({
                        id: "enterEmail",
                        defaultMessage: "ادخل البريد الإلكتروني",
                      })}
                    />
                  </Form.Item>
                </Col>
                <Col
                  className="gutter-row"
                  xl={8}
                  lg={12}
                  md={12}
                  xs={24}
                  sm={8}
                  span={11}
                >
                  <Form.Item
                    name="phone"
                    label={intl.formatMessage({
                      id: "phoneNumber",
                      defaultMessage: "رقم جوال ",
                    })}
                    rules={
                      [
                        // {
                        //   required: true,
                        //   message: intl.formatMessage({
                        //     id: "phoneRequired",
                        //     defaultMessage: "رقم جوال مطلوب",
                        //   }),
                        // },
                        // {
                        //   validator: (rule, value) => {
                        //     console.log(value, "value");
                        //     if (isValidPhoneNumber(value)) {
                        //       return Promise.resolve();
                        //     }
                        //     return Promise.reject(
                        //       intl.formatMessage({
                        //         id: "phoneInvalid",
                        //         defaultMessage: "رقم الجوال غير صالح",
                        //       })
                        //     );
                        //   },
                        // },
                      ]
                    }
                    required={false}
                    className="w-full"
                    hasFeedback
                  >
                    <PhoneInputWithCountrySelect
                      defaultCountry="SA"
                      className="w-full"
                      onChange={handlePhoneNumberChange}
                      onCountryChange={handleCountryChange}
                      name="phone_country_code"
                      international
                      hasFeedback
                      limitMaxLength={true}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col
                  className="gutter-row"
                  xl={12}
                  lg={12}
                  md={12}
                  xs={24}
                  sm={8}
                  span={11}
                >
                  <Form.Item
                    name="start_date"
                    label={intl.formatMessage({
                      id: "startDate",
                      defaultMessage: "تاريخ البداية",
                    })}
                    className="w-full"
                    required={false}
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "startDateRequired",
                          defaultMessage: "تاريخ البداية مطلوب",
                        }),
                      },
                    ]}
                  >
                    <DatePicker
                      placeholder={intl.formatMessage({
                        id: "pickDate",
                        defaultMessage: "إختر التأريخ",
                      })}
                    />
                  </Form.Item>
                </Col>
                <Col
                  className="gutter-row"
                  xl={12}
                  lg={12}
                  md={12}
                  xs={24}
                  sm={8}
                  span={11}
                >
                  <Form.Item
                    name="end_date"
                    label={intl.formatMessage({
                      id: "endDate",
                      defaultMessage: "تاريخ النهاية",
                    })}
                    className="w-full"
                    required={false}
                    rules={[
                      {
                        required: false,
                        message: intl.formatMessage({
                          id: "endDateRequired",
                          defaultMessage: "تاريخ النهاية مطلوب",
                        }),
                      },
                    ]}
                  >
                    <DatePicker
                      placeholder={intl.formatMessage({
                        id: "pickDate",
                        defaultMessage: "إختر التأريخ",
                      })}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col
                  className="gutter-row"
                  xl={24}
                  lg={12}
                  md={12}
                  xs={24}
                  sm={8}
                  span={11}
                >
                  <Form.Item
                    name={["notes", "ar"]}
                    label={intl.formatMessage({
                      id: "notes",
                      defaultMessage: "ملاحظات",
                    })}
                    required={false}
                    className="w-full"
                  >
                    <Input.TextArea
                      size="large"
                      rows={2}
                      placeholder={intl.formatMessage({
                        id: "enterNotes",
                        defaultMessage: "أدخل ملاحظات",
                      })}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Flex gap="small" justify="flex-end">
                <Button
                  type="primary"
                  loading={isLoading}
                  className="text-white w-[164.5px]"
                  htmlType="submit"
                >
                  {intl.formatMessage({
                    id: "saveChanges",
                    defaultMessage: "حفظ التغييرات",
                  })}
                </Button>
                <Button
                  type="primary"
                  onClick={() => setEditing(!editing)}
                  className="bg-color-light text-black w-[164.5px]"
                  htmlType="button"
                >
                  {intl.formatMessage({
                    id: "cancel",
                    defaultMessage: "إلفاء",
                  })}
                </Button>
              </Flex>
            </Form>
          </div>
        )}
      </div>
    </>
  );
};

export default BasicInfo;
